/**
 * AnnounceModalityModal (MED-563 E18-5)
 *
 * Two-step modal for announcing a modality to participants:
 * 1. Validate prerequisites (show errors if not met)
 * 2. Choose to send SGOS invite emails or skip
 */

import { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  Stack,
  Text,
  Group,
  Alert,
  List,
  Loader,
  Center,
  Divider,
  ThemeIcon,
  Box,
} from '@mantine/core';
import { AlertCircle, CheckCircle, Mail, SkipForward, AlertTriangle, Copy } from 'react-feather';
import { notifications } from '@mantine/notifications';
import { cohortsApi } from '../../../../services/api/cohorts';
import type { ModalityType } from '../../../../types';

const MODALITY_LABELS: Record<ModalityType, string> = {
  group_coaching: 'Group Coaching',
  one_on_one: '1:1 Coaching',
  workshop: 'Workshops',
};

interface AnnounceModalityModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  modalityId: string;
  modalityType: ModalityType;
  onSuccess: (modalityId: string) => void;
  emailDisabled?: boolean;
}

export function AnnounceModalityModal({
  opened,
  onClose,
  cohortId,
  modalityId,
  modalityType,
  onSuccess,
  emailDisabled,
}: AnnounceModalityModalProps) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpened, setConfirmOpened] = useState(false);
  const [prerequisites, setPrerequisites] = useState<{
    valid: boolean;
    errors: string[];
  } | null>(null);

  const label = MODALITY_LABELS[modalityType] ?? modalityType;

  // Load prerequisites when modal opens
  useEffect(() => {
    if (!opened) return;
    setPrerequisites(null);
    setLoading(true);
    cohortsApi
      .getAnnouncePrerequisites(cohortId, modalityId)
      .then(setPrerequisites)
      .catch(() => {
        setPrerequisites({ valid: false, errors: ['Failed to check prerequisites. Please try again.'] });
      })
      .finally(() => setLoading(false));
  }, [opened, cohortId, modalityId]);

  const handleAnnounce = async (sendInvites: boolean) => {
    setSubmitting(true);
    try {
      await cohortsApi.announceModality(cohortId, modalityId, sendInvites);
      notifications.show({
        title: `${label} Announced`,
        message: sendInvites
          ? `${label} is now visible to participants and invitation emails have been sent.`
          : `${label} is now visible to participants. Invitation emails were skipped.`,
        color: 'green',
      });
      onSuccess(modalityId);
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Announcement Failed',
        message: error instanceof Error ? error.message : 'Please try again.',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Finalize & Announce ${label}`}
      size="md"
    >
      {loading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : prerequisites && !prerequisites.valid ? (
        <Stack gap="md">
          <Alert
            icon={<AlertCircle size={16} />}
            title="Prerequisites Not Met"
            color="red"
            variant="light"
          >
            <Text size="sm" mb="xs">
              The following items must be resolved before announcing {label}:
            </Text>
            <List size="sm" spacing={4}>
              {prerequisites.errors.map((err, i) => (
                <List.Item key={i}>{err}</List.Item>
              ))}
            </List>
          </Alert>
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap="md">
          <Alert
            icon={<CheckCircle size={16} />}
            title="Ready to Announce"
            color="green"
            variant="light"
          >
            <Text size="sm">
              All prerequisites for <strong>{label}</strong> are satisfied. Once announced, participants
              will be able to see this modality on their dashboard.
            </Text>
          </Alert>

          {/* MED-579: Email disabled notice */}
          {emailDisabled && (
            <Alert icon={<AlertTriangle size={16} />} color="yellow" variant="light">
              <Text size="sm">
                Announcement emails have been turned off for this cohort. You can still announce
                without sending emails, or copy the email template for manual sending.
              </Text>
            </Alert>
          )}

          <Box
            style={{
              border: '1px solid #E8E6E1',
              borderRadius: 8,
              padding: '16px',
              backgroundColor: '#FAFAF8',
            }}
          >
            <Text size="sm" fw={600} mb="xs">
              Send SGOS Invitation Emails?
            </Text>
            <Text size="sm" c="dimmed" mb="md">
              {emailDisabled
                ? 'Emails are currently disabled. You can announce without emails or copy the template.'
                : 'You can send magic link emails to all active participants now, or skip and let them access their dashboard when they log in.'}
            </Text>

            <Divider mb="md" />

            <Stack gap="sm">
              {!emailDisabled && (
                <Button
                  leftSection={
                    <ThemeIcon color="medleyGreen" variant="transparent" size="sm">
                      <Mail size={14} />
                    </ThemeIcon>
                  }
                  onClick={() => setConfirmOpened(true)}
                  loading={submitting}
                  disabled={submitting}
                  fullWidth
                  variant="filled"
                >
                  Announce &amp; Send Invite Emails
                </Button>
              )}
              <Button
                leftSection={
                  <ThemeIcon color={emailDisabled ? 'medleyGreen' : 'gray'} variant="transparent" size="sm">
                    <SkipForward size={14} />
                  </ThemeIcon>
                }
                onClick={() => handleAnnounce(false)}
                loading={submitting}
                disabled={submitting}
                fullWidth
                variant={emailDisabled ? 'filled' : 'default'}
                color={emailDisabled ? 'medleyGreen' : undefined}
              >
                Announce Without Sending Emails
              </Button>
              {emailDisabled && (
                <Button
                  leftSection={
                    <ThemeIcon color="gray" variant="transparent" size="sm">
                      <Copy size={14} />
                    </ThemeIcon>
                  }
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `Your ${label} program is ready! Log in to your dashboard to view your schedule and session details.`,
                    );
                    notifications.show({
                      title: 'Copied',
                      message: 'Email template copied to clipboard',
                      color: 'green',
                    });
                  }}
                  fullWidth
                  variant="default"
                >
                  Copy Email Template
                </Button>
              )}
            </Stack>
          </Box>
        </Stack>
      )}
    </Modal>

    {/* MED-646: Bulk send confirmation dialog */}
    <Modal
      opened={confirmOpened}
      onClose={() => setConfirmOpened(false)}
      title="Confirm Email Send"
      size="sm"
      centered
      zIndex={301}
    >
      <Stack gap="md">
        <Alert icon={<AlertTriangle size={16} />} color="yellow" variant="light">
          <Text size="sm">
            You are about to announce <Text component="span" fw={700}>{label}</Text> and
            send invitation emails to all active participants. This action cannot be undone.
          </Text>
        </Alert>
        <Group justify="flex-end" gap="sm">
          <Button
            variant="default"
            onClick={() => setConfirmOpened(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            color="medleyGreen"
            leftSection={<Mail size={14} />}
            onClick={() => {
              setConfirmOpened(false);
              handleAnnounce(true);
            }}
            loading={submitting}
            disabled={submitting}
          >
            Confirm &amp; Send
          </Button>
        </Group>
      </Stack>
    </Modal>
    </>
  );
}
